import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { StoreAsset } from '~/constants/stores';
import { safeNavigate } from '~/pages/Onboarding/store/onboarding.sagas';
import { call, put, takeEvery } from 'redux-saga/effects';
import { selectState } from '~/sagas/index';
import { ApiError, getStoreConnectUrl } from '~/services/store-connect.service';
import { StoreName } from '~/types/Store';
import * as storeConnectActions from './store-connect.actions';
import {
  fetchStoreConnectUrl,
  fetchStoreConnectUrlError,
  fetchStoreConnectUrlSuccess,
} from './store-connect.actions';
import { StoreConnectAPIErrors } from './store-connect.reducers';
import { getStoreConnectSelection } from './store-connect.selectors';

export function* startStoreConnection(
  action: ReturnType<typeof storeConnectActions.startStoreConnect>,
) {
  // stores that have been migrated to store-connect
  // Amazon uses store-connect to return redirect url
  const isUsingNewStoreConnectFlow = (name) => {
    return [StoreName.Wix, StoreName.Amazon, StoreName.Etsy, StoreName.Walmart].includes(name);
  };

  // stores using the new store-connect flow
  if (isUsingNewStoreConnectFlow(action.payload.storeSelection.name)) {
    action.payload.readyForUrl
      ? yield put(
          fetchStoreConnectUrl({
            ...action.payload.params,
            shippo_location: action.payload.shippoLocation,
          }),
        )
      : yield put(
          fetchStoreConnectUrlSuccess(
            action.payload.storeSelection.connectRoute ?? '',
            '?shippo_location=' + encodeURIComponent(action.payload.shippoLocation),
          ),
        );
  } else {
    // stores with old store-connect flow
    yield put(fetchStoreConnectUrlSuccess(action.payload.storeSelection.connectLink, ''));
  }
}

export function* fetchUrlFromStoreConnect(
  action: ReturnType<typeof storeConnectActions.fetchStoreConnectUrl>,
) {
  const storeSelection: ReturnType<typeof getStoreConnectSelection> =
    yield selectState<StoreAsset | null>(getStoreConnectSelection);
  const store = storeSelection ? storeSelection.name : 'error';

  try {
    const response: AxiosResponse<{ redirectUrl: string }> = yield call(
      getStoreConnectUrl,
      store.toLowerCase(),
      action.payload.params,
    );
    yield put(fetchStoreConnectUrlSuccess(response.data.redirectUrl, ''));
  } catch (error) {
    const errorCode = (error as ApiError).response?.data.error;
    const errorMsgFunc =
      StoreConnectAPIErrors[errorCode as keyof typeof StoreConnectAPIErrors] ??
      StoreConnectAPIErrors['CATCH_ALL'];
    yield put(fetchStoreConnectUrlError(errorMsgFunc(store)));
  }
}

export function* storeConnectRedirectSuccess(
  action: ReturnType<typeof storeConnectActions.fetchStoreConnectUrlSuccess>,
) {
  if (action.payload.redirectUrl.indexOf('/') === 0) {
    yield put(push({ pathname: action.payload.redirectUrl, search: action.payload.queryString }));
  } else {
    window.location.href = action.payload.redirectUrl;
  }
}

export function* storeConnectRedirectError() {
  if (window.location.pathname.match('^/onboarding')) yield safeNavigate('/orders');
}

export default [
  takeEvery(storeConnectActions.StoreConnectActionKeys.START_STORE_CONNECT, startStoreConnection),
  takeEvery(
    storeConnectActions.StoreConnectActionKeys.STORE_CONNECT_URL_FETCH,
    fetchUrlFromStoreConnect,
  ),
  takeEvery(
    storeConnectActions.StoreConnectActionKeys.STORE_CONNECT_URL_FETCH_ERROR,
    storeConnectRedirectError,
  ),
  takeEvery(
    storeConnectActions.StoreConnectActionKeys.STORE_CONNECT_URL_FETCH_SUCCESS,
    storeConnectRedirectSuccess,
  ),
];
